import {
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import DataTable from "components/table/DataTable";

import {
    getVisits,
    getVisit,
    getVisitRoute,
    getVisitOverview,
    addVisitRoutePoint,
    deleteVisit,
    startVisit,
    submitVisit,
    approveVisit,
    rejectVisit,
    requestVisitCorrection,
} from "services/api/visitApi";

import {
    getEmployees,
} from "services/api/employeeApi";

import {
    showSuccess,
    showError,
} from "utils/toast";

import {
    visitColumns,
    overviewColumns,
} from "./columns";

import VisitModal from "./VisitModal";


/*
|--------------------------------------------------------------------------
| GPS CONFIGURATION
|--------------------------------------------------------------------------
|
| Tracking point will be saved when:
|
| - salesperson moves at least 50 meters
| OR
| - at least 30 seconds have passed
|
| This avoids calling the API for every browser GPS update.
|
*/

const GPS_MIN_DISTANCE_METERS = 50;

const GPS_MIN_INTERVAL_MS = 30 * 1000;


/*
|--------------------------------------------------------------------------
| ACTIVE VISIT STORAGE KEY
|--------------------------------------------------------------------------
*/

const ACTIVE_TRACKING_KEY = "active_visit_tracking_id";


/*
|--------------------------------------------------------------------------
| HAVERSINE DISTANCE
|--------------------------------------------------------------------------
|
| Frontend uses this ONLY to decide whether a new point should
| be sent to backend.
|
| Backend remains the official source for stored KM calculation.
|
*/

const calculateDistanceMeters = (
    latitude1,
    longitude1,
    latitude2,
    longitude2
) => {

    const earthRadiusMeters = 6371000;

    const toRadians = (value) => {return ((value * Math.PI) /180);};

    const dLatitude = toRadians(latitude2 - latitude1);

    const dLongitude = toRadians(longitude2 - longitude1);

    const lat1 = toRadians(latitude1);

    const lat2 = toRadians(latitude2);

    const a = Math.sin(dLatitude / 2) * Math.sin(dLatitude / 2) +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLongitude / 2) * Math.sin(dLongitude / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return (earthRadiusMeters * c);
};

/*
|--------------------------------------------------------------------------
| REVERSE GEOCODING
|--------------------------------------------------------------------------
|
| Used for START and END points.
|
| Tracking points do not need an address for every GPS update.
| Their coordinates are enough for the actual route.
|
*/

const getAddressFromCoordinates = async (latitude,longitude) => {

    try {

        const response =
            await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(
                    latitude
                )}&lon=${encodeURIComponent(
                    longitude
                )}&zoom=18&addressdetails=1`,
                {
                    headers: {
                        Accept:
                            "application/json",
                    },
                }
            );

        if (!response.ok) {
            return "";
        }

        const data =
            await response.json();

        /*
        |--------------------------------------------------------------------------
        | Prefer display_name
        |--------------------------------------------------------------------------
        */

        if (
            data?.display_name
        ) {

            return data.display_name;

        }

        /*
        |--------------------------------------------------------------------------
        | Fallback address construction
        |--------------------------------------------------------------------------
        */

        const address =
            data?.address || {};

        return [
            address.road,
            address.neighbourhood,
            address.suburb,
            address.city ||
                address.town ||
                address.village,
            address.state,
            address.postcode,
        ]
            .filter(Boolean)
            .join(", ");

    } catch (error) {

        console.warn(
            "Reverse geocoding failed:",
            error
        );

        return "";

    }

};


export default function Visits() {

    // ============================================================
    // MODE
    // ============================================================

    const [
        viewMode,
        setViewMode,
    ] = useState("overview");


    // ============================================================
    // VISITS
    // ============================================================

    const [
        visits,
        setVisits,
    ] = useState([]);


    // ============================================================
    // OVERVIEW
    // ============================================================

    const [
        overview,
        setOverview,
    ] = useState([]);


    // ============================================================
    // LOADING
    // ============================================================

    const [
        loading,
        setLoading,
    ] = useState(false);


    // ============================================================
    // PAGE
    // ============================================================

    const [
        page,
        setPage,
    ] = useState(1);


    // ============================================================
    // MODAL
    // ============================================================

    const [
        openModal,
        setOpenModal,
    ] = useState(false);


    // ============================================================
    // LOGGED-IN USER
    // ============================================================

    const loggedInUser = useMemo(() => {

        try {

            return JSON.parse(
                localStorage.getItem(
                    "user"
                ) || "{}"
            );

        } catch (error) {

            console.error(
                "Unable to parse logged-in user.",
                error
            );

            return {};

        }

    }, []);


    // ============================================================
    // ROLE
    // ============================================================

    const isSalesPerson =
        Array.isArray(
            loggedInUser?.roles
        ) &&
        loggedInUser.roles.includes(
            "sales_person"
        );


    // ============================================================
    // EMPLOYEES
    // ============================================================

    const [
        employees,
        setEmployees,
    ] = useState([]);


    const [
        employeeLoading,
        setEmployeeLoading,
    ] = useState(false);

    const [editingVisit, setEditingVisit] = useState(null);


    // ============================================================
    // FILTERS
    // ============================================================

    const [
        filters,
        setFilters,
    ] = useState({

        employeeId:
            isSalesPerson
                ? String(
                    loggedInUser?.employee?.id ||
                    ""
                )
                : "",

        fromDate:
            "",

        toDate:
            "",

        status:
            "",

    });


    // ============================================================
    // GPS REFS
    // ============================================================

    const trackingWatchIdRef =
        useRef(null);

    const trackingVisitIdRef =
        useRef(null);

    const trackingStartedRef =
        useRef(false);

    const lastRecordedPointRef =
        useRef(null);

    const trackingRequestInProgressRef =
        useRef(false);


    // ============================================================
    // STOP ONLY GPS WATCHER
    // ============================================================

    const stopGpsWatcher = () => {

        if (
            trackingWatchIdRef.current !==
            null
        ) {

            navigator.geolocation.clearWatch(
                trackingWatchIdRef.current
            );

            trackingWatchIdRef.current =
                null;

        }

        trackingStartedRef.current =
            false;

    };


    // ============================================================
    // STOP COMPLETE GPS TRACKING
    // ============================================================

    const stopGpsTracking = () => {

        stopGpsWatcher();


        trackingVisitIdRef.current =
            null;


        lastRecordedPointRef.current =
            null;


        trackingRequestInProgressRef.current =
            false;


        try {

            localStorage.removeItem(
                ACTIVE_TRACKING_KEY
            );

        } catch (error) {

            console.error(
                "Unable to clear active tracking storage:",
                error
            );

        }

    };


    // ============================================================
    // GET CURRENT GPS
    // ============================================================

    const getCurrentPosition =
        () =>
            new Promise(
                (
                    resolve,
                    reject
                ) => {

                    if (
                        !navigator.geolocation
                    ) {

                        reject({
                            code: -1,
                            message:
                                "GPS is not supported.",
                        });

                        return;

                    }


                    navigator.geolocation.getCurrentPosition(
                        resolve,
                        reject,
                        {

                            enableHighAccuracy:
                                true,

                            timeout:
                                15000,

                            maximumAge:
                                0,

                        }
                    );

                }
            );


    // ============================================================
    // SAVE ROUTE POINT
    // ============================================================

    const saveRoutePoint = async (
        visitId,
        position,
        pointType = "tracking",
        force = false
    ) => {

        if (!visitId) {
            return false;
        }


        if (!position?.coords) {
            return false;
        }


        const latitude =
            Number(
                position.coords.latitude
            );

        const longitude =
            Number(
                position.coords.longitude
            );


        if (
            !Number.isFinite(
                latitude
            ) ||
            !Number.isFinite(
                longitude
            )
        ) {

            return false;

        }


        /*
        |--------------------------------------------------------------------------
        | Normal tracking request.
        |--------------------------------------------------------------------------
        */

        if (
            trackingRequestInProgressRef.current &&
            !force
        ) {

            return false;

        }


        /*
        |--------------------------------------------------------------------------
        | Final point.
        |
        | If another request is currently running, wait for it.
        |--------------------------------------------------------------------------
        */

        if (
            trackingRequestInProgressRef.current &&
            force
        ) {

            let attempts = 0;

            while (
                trackingRequestInProgressRef.current &&
                attempts < 50
            ) {

                await new Promise(
                    (resolve) =>
                        setTimeout(
                            resolve,
                            100
                        )
                );

                attempts++;

            }

        }


        /*
        |--------------------------------------------------------------------------
        | If still busy, don't submit incomplete route.
        |--------------------------------------------------------------------------
        */

        if (
            trackingRequestInProgressRef.current
        ) {

            return false;

        }


        trackingRequestInProgressRef.current =
            true;


        try {

            /*
            |--------------------------------------------------------------------------
            | Address:
            |
            | Start / End → reverse geocoding.
            | Tracking → no address request.
            |--------------------------------------------------------------------------
            */

            let address = "";

            if (
                pointType === "start" ||
                pointType === "end"
            ) {

                address =
                    await getAddressFromCoordinates(
                        latitude,
                        longitude
                    );

            }


            const response =
                await addVisitRoutePoint(
                    visitId,
                    {

                        latitude,

                        longitude,

                        address,

                        pointType,

                    }
                );


            console.log(
                `ROUTE ${pointType.toUpperCase()} SAVED:`,
                response
            );


            /*
            |--------------------------------------------------------------------------
            | Remember successful GPS point.
            |--------------------------------------------------------------------------
            */

            lastRecordedPointRef.current = {

                latitude,

                longitude,

                recordedAt:
                    Date.now(),

            };


            return true;

        } catch (error) {

            console.error(
                `Unable to save ${pointType} route point:`,
                error
            );

            return false;

        } finally {

            trackingRequestInProgressRef.current =
                false;

        }

    };


    // ============================================================
    // SHOULD SAVE TRACKING POINT
    // ============================================================

    const shouldRecordGpsPoint = (
        latitude,
        longitude
    ) => {

        const lastPoint =
            lastRecordedPointRef.current;


        /*
        |--------------------------------------------------------------------------
        | First tracking point
        |--------------------------------------------------------------------------
        */

        if (!lastPoint) {
            return true;
        }


        const elapsed =
            Date.now() -
            lastPoint.recordedAt;


        const distanceMeters =
            calculateDistanceMeters(
                lastPoint.latitude,
                lastPoint.longitude,
                latitude,
                longitude
            );


        /*
        |--------------------------------------------------------------------------
        | Time rule
        |--------------------------------------------------------------------------
        */

        if (
            elapsed >=
            GPS_MIN_INTERVAL_MS
        ) {

            return true;

        }


        /*
        |--------------------------------------------------------------------------
        | Movement rule
        |--------------------------------------------------------------------------
        */

        if (
            distanceMeters >=
            GPS_MIN_DISTANCE_METERS
        ) {

            return true;

        }


        return false;

    };


    // ============================================================
    // START GPS WATCH
    // ============================================================

    const startGpsTracking = (
        visitId,
        options = {}
    ) => {

        if (
            !navigator.geolocation
        ) {

            console.error(
                "Geolocation is not supported."
            );

            return false;

        }


        /*
        |--------------------------------------------------------------------------
        | Stop old watcher first.
        |--------------------------------------------------------------------------
        */

        if (
            trackingWatchIdRef.current !==
            null
        ) {

            navigator.geolocation.clearWatch(
                trackingWatchIdRef.current
            );

            trackingWatchIdRef.current =
                null;

        }


        trackingVisitIdRef.current =
            String(visitId);


        trackingStartedRef.current =
            true;


        /*
        |--------------------------------------------------------------------------
        | Persist active visit.
        |--------------------------------------------------------------------------
        */

        try {

            localStorage.setItem(
                ACTIVE_TRACKING_KEY,
                String(visitId)
            );

        } catch (error) {

            console.error(
                "Unable to save active tracking:",
                error
            );

        }


        /*
        |--------------------------------------------------------------------------
        | Set initial reference point.
        |--------------------------------------------------------------------------
        |
        | We do NOT save another start point here.
        | handleStart() already saves the start point.
        |
        */

        if (
            options.initialPosition?.coords
        ) {

            const initialLatitude =
                Number(
                    options.initialPosition
                        .coords
                        .latitude
                );

            const initialLongitude =
                Number(
                    options.initialPosition
                        .coords
                        .longitude
                );

            if (
                Number.isFinite(
                    initialLatitude
                ) &&
                Number.isFinite(
                    initialLongitude
                )
            ) {

                lastRecordedPointRef.current = {

                    latitude:
                        initialLatitude,

                    longitude:
                        initialLongitude,

                    recordedAt:
                        Date.now(),

                };

            }

        }


        /*
        |--------------------------------------------------------------------------
        | WATCH POSITION
        |--------------------------------------------------------------------------
        */

        trackingWatchIdRef.current =
            navigator.geolocation.watchPosition(

                async (position) => {

                    if (
                        !trackingStartedRef.current
                    ) {

                        return;

                    }


                    const activeVisitId =
                        trackingVisitIdRef.current;


                    if (!activeVisitId) {
                        return;
                    }


                    const latitude =
                        Number(
                            position.coords.latitude
                        );

                    const longitude =
                        Number(
                            position.coords.longitude
                        );


                    if (
                        !Number.isFinite(
                            latitude
                        ) ||
                        !Number.isFinite(
                            longitude
                        )
                    ) {

                        return;

                    }


                    /*
                    |--------------------------------------------------------------------------
                    | Don't send every GPS update.
                    |--------------------------------------------------------------------------
                    */

                    if (
                        !shouldRecordGpsPoint(
                            latitude,
                            longitude
                        )
                    ) {

                        return;

                    }


                    await saveRoutePoint(
                        activeVisitId,
                        position,
                        "tracking",
                        false
                    );

                },

                (error) => {

                    console.error(
                        "GPS tracking error:",
                        error
                    );

                },

                {

                    enableHighAccuracy:
                        true,

                    timeout:
                        15000,

                    maximumAge:
                        10000,

                }
            );


        console.log(
            "GPS TRACKING STARTED:",
            visitId
        );


        return true;

    };




    // ============================================================
    // RESUME ACTIVE TRACKING
    // ============================================================

    const resumeActiveTracking = async () => {
        try {
            const storedVisitId = localStorage.getItem(
                ACTIVE_TRACKING_KEY
            );

            if (!storedVisitId) {
                return;
            }

            // STEP 1: Verify visit status
            const visitResponse = await getVisit(storedVisitId);

            const visit =
                visitResponse?.data?.visit ||
                visitResponse?.data ||
                visitResponse?.visit ||
                null;

            if (
                !visit ||
                String(visit.status).toLowerCase() !== "started"
            ) {
                stopGpsTracking();
                return;
            }

            if (!navigator.geolocation) {
                console.warn("GPS is not supported.");
                return;
            }

            // STEP 2: Fetch persisted route points
            const routeResponse = await getVisitRoute(storedVisitId);

            const route =
                routeResponse?.data?.data ||
                routeResponse?.data ||
                routeResponse ||
                {};

            const points = Array.isArray(route?.points)
                ? route.points
                : [];

            // STEP 3: Restore last saved point from backend
            if (points.length > 0) {
                const lastPoint = [...points].sort(
                    (a, b) =>
                        Number(a.sequence || 0) -
                        Number(b.sequence || 0)
                ).at(-1);

                const latitude = Number(lastPoint?.latitude);
                const longitude = Number(lastPoint?.longitude);

                if (
                    Number.isFinite(latitude) &&
                    Number.isFinite(longitude)
                ) {
                    lastRecordedPointRef.current = {
                        latitude,
                        longitude,
                        recordedAt: lastPoint?.createdAt
                            ? new Date(lastPoint.createdAt).getTime()
                            : Date.now(),
                    };
                }
            } else {
                lastRecordedPointRef.current = null;
            }

            // STEP 4: Get current GPS position
            const currentPosition = await getCurrentPosition();

            // STEP 5: Start watcher with restored reference
            trackingVisitIdRef.current = String(storedVisitId);
            trackingStartedRef.current = true;

            // Save current location against last persisted point.
            // This captures movement during refresh/navigation.
            const currentSaved = await saveRoutePoint(
                storedVisitId,
                currentPosition,
                "tracking",
                true
            );

            if (!currentSaved) {
                console.warn(
                    "Current GPS position could not be saved during resume."
                );
            }

            // STEP 6: Start continuous GPS watcher
            startGpsTracking(storedVisitId);

            console.log(
                "GPS TRACKING RESUMED:",
                storedVisitId
            );

        } catch (error) {
            console.error(
                "Unable to resume GPS tracking:",
                error
            );

            // Keep localStorage intact so a temporary GPS/API
            // failure does not permanently lose the active visit.
        }
    };


    // ============================================================
    // GPS CLEANUP / RESUME
    // ============================================================

    useEffect(() => {

        resumeActiveTracking();


        return () => {

            /*
            |--------------------------------------------------------------------------
            | Stop browser watcher when component unmounts.
            |
            | Do NOT remove localStorage here.
            | This allows resume after page refresh/navigation.
            |--------------------------------------------------------------------------
            */

            if (
                trackingWatchIdRef.current !==
                null
            ) {

                navigator.geolocation.clearWatch(
                    trackingWatchIdRef.current
                );

                trackingWatchIdRef.current =
                    null;

            }

        };

    }, []);


    // ============================================================
    // LOAD EMPLOYEES
    // ============================================================

    useEffect(() => {

        if (isSalesPerson) {

            setEmployees([]);

            return;

        }


        const loadEmployees =
            async () => {

                try {

                    setEmployeeLoading(
                        true
                    );


                    const response =
                        await getEmployees(
                            1,
                            100
                        );


                    const employeeData =
                        response?.data?.data?.employees ||
                        response?.data?.employees ||
                        response?.data?.data?.records ||
                        response?.data?.records ||
                        [];


                    setEmployees(
                        Array.isArray(
                            employeeData
                        )
                            ? employeeData
                            : []
                    );

                } catch (error) {

                    console.error(
                        "Visit employee filter error:",
                        error
                    );


                    setEmployees([]);


                    showError(
                        error?.response?.data?.message ||
                        "Unable to load employees."
                    );

                } finally {

                    setEmployeeLoading(
                        false
                    );

                }

            };


        loadEmployees();

    }, [
        isSalesPerson,
    ]);


    // ============================================================
    // EMPLOYEE OPTIONS
    // ============================================================

    const employeeOptions =
        useMemo(() => {

            if (isSalesPerson) {

                const employee =
                    loggedInUser?.employee;


                if (!employee?.id) {
                    return [];
                }


                return [

                    {
                        value:
                            String(
                                employee.id
                            ),

                        label:
                            loggedInUser?.name ||
                            employee?.name ||
                            employee?.employeeCode ||
                            `Employee ${employee.id}`,

                    },

                ];

            }


            return [

                {
                    value:
                        "",

                    label:
                        "All Salespersons",
                },


                ...employees
                    .filter(Boolean)
                    .map(
                        (employee) => ({

                            value:
                                String(
                                    employee.id
                                ),

                            label:
                                employee?.user?.name ||
                                employee?.name ||
                                employee?.employeeCode ||
                                `Employee ${employee.id}`,

                        })
                    ),

            ];

        }, [
            isSalesPerson,
            loggedInUser,
            employees,
        ]);


    // ============================================================
    // STATUS OPTIONS
    // ============================================================

    const statusOptions = [

        {
            value:
                "",

            label:
                "All Status",
        },

        {
            value:
                "planned",

            label:
                "Planned",
        },

        {
            value:
                "started",

            label:
                "Started",
        },

        {
            value:
                "submitted",

            label:
                "Submitted",
        },

        {
            value:
                "approved",

            label:
                "Approved",
        },

        {
            value:
                "rejected",

            label:
                "Rejected",
        },

        {
            value:
                "correction_requested",

            label:
                "Correction Requested",
        },

        {
            value:
                "cancelled",

            label:
                "Cancelled",
        },

    ];


    // ============================================================
    // OVERVIEW FILTERS
    // ============================================================

    const overviewFilters =
        useMemo(() => {

            const params = {};


            /*
            |--------------------------------------------------------------------------
            | Daily overview.
            |--------------------------------------------------------------------------
            */

            if (
                filters.fromDate
            ) {

                params.visitDate =
                    filters.fromDate;

            }


            /*
            |--------------------------------------------------------------------------
            | Employee.
            |--------------------------------------------------------------------------
            */

            if (
                filters.employeeId
            ) {

                params.employeeId =
                    filters.employeeId;

            }


            return params;

        }, [
            filters.fromDate,
            filters.employeeId,
        ]);


    // ============================================================
    // LOAD OVERVIEW
    // ============================================================

    const loadOverview =
        async () => {

            try {

                setLoading(
                    true
                );


                const response =
                    await getVisitOverview(
                        overviewFilters
                    );


                console.log(
                    "VISIT OVERVIEW RESPONSE:",
                    response
                );


                const overviewData =
                    response?.data?.overview ||
                    response?.data?.data?.overview ||
                    [];


                setOverview(
                    Array.isArray(
                        overviewData
                    )
                        ? overviewData
                        : []
                );


            } catch (error) {

                console.error(
                    "VISIT OVERVIEW ERROR:",
                    error
                );


                setOverview([]);


                showError(
                    error?.response?.data?.message ||
                    "Unable to load visit overview."
                );

            } finally {

                setLoading(
                    false
                );

            }

        };


    // ============================================================
    // LOAD VISITS
    // ============================================================

    const loadVisits =
        async () => {

            try {

                setLoading(
                    true
                );


                const response =
                    await getVisits(
                        page,
                        10,
                        filters
                    );


                console.log(
                    "VISITS RESPONSE:",
                    response
                );


                setVisits(
                    response?.data?.visits ||
                    []
                );


            } catch (error) {

                console.error(
                    "VISIT LIST ERROR:",
                    error
                );


                setVisits([]);


                showError(
                    error?.response?.data?.message ||
                    "Unable to load visits."
                );

            } finally {

                setLoading(
                    false
                );

            }

        };


    // ============================================================
    // LOAD LIST / OVERVIEW WHEN FILTERS CHANGE
    // ============================================================

    useEffect(() => {

        if (
            viewMode === "overview"
        ) {

            loadOverview();

        } else {

            loadVisits();

        }

    }, [
        viewMode,
        page,
        filters.employeeId,
        filters.fromDate,
        filters.toDate,
        filters.status,
    ]);


    // ============================================================
    // FILTER CHANGE
    // ============================================================

    const handleFilterChange =
        (
            name,
            value
        ) => {

            setPage(1);


            setFilters(
                (previous) => ({

                    ...previous,

                    [name]:
                        value,

                })
            );

        };


    // ============================================================
    // CLEAR FILTERS
    // ============================================================

    const clearFilters =
        () => {

            setPage(1);


            setFilters({

                employeeId:
                    isSalesPerson
                        ? String(
                            loggedInUser?.employee?.id ||
                            ""
                        )
                        : "",

                fromDate:
                    "",

                toDate:
                    "",

                status:
                    "",

            });

        };


    // ============================================================
    // VIEW SALESPERSON
    // ============================================================

    const handleViewEmployee =
        (
            employeeId
        ) => {

            if (!employeeId) {
                return;
            }


            setFilters(
                (previous) => ({

                    ...previous,

                    employeeId:
                        String(
                            employeeId
                        ),

                })
            );


            setViewMode(
                "visits"
            );


            setPage(1);

        };


    // ============================================================
    // BACK TO OVERVIEW
    // ============================================================

    const handleBackToOverview =
        () => {

            setViewMode(
                "overview"
            );


            setPage(1);

        };


    // ============================================================
    // DELETE / CANCEL
    // ============================================================

    const handleDelete =
        async (
            id
        ) => {

            const confirmed =
                window.confirm(
                    "Are you sure you want to cancel this visit?"
                );


            if (!confirmed) {
                return;
            }


            try {

                /*
                |--------------------------------------------------------------------------
                | Stop GPS if this is active Visit.
                |--------------------------------------------------------------------------
                */

                if (
                    String(
                        trackingVisitIdRef.current
                    ) ===
                    String(id)
                ) {

                    stopGpsTracking();

                }


                await deleteVisit(
                    id
                );


                showSuccess(
                    "Visit cancelled successfully."
                );


                if (
                    viewMode === "overview"
                ) {

                    await loadOverview();

                } else {

                    await loadVisits();

                }

            } catch (error) {

                console.error(
                    error
                );


                showError(
                    error?.response?.data?.message ||
                    "Unable to cancel visit."
                );

            }

        };


    // ============================================================
    // START VISIT
    // ============================================================

    const handleStart =
        async (
            id
        ) => {

            try {

                /*
                |--------------------------------------------------------------------------
                | Check browser GPS support.
                |--------------------------------------------------------------------------
                */

                if (
                    !navigator.geolocation
                ) {

                    showError(
                        "GPS is required to start the visit."
                    );

                    return;

                }


                /*
                |--------------------------------------------------------------------------
                | Get current position.
                |--------------------------------------------------------------------------
                */

                const position =
                    await getCurrentPosition();


                const latitude =
                    String(
                        position.coords.latitude
                    );

                const longitude =
                    String(
                        position.coords.longitude
                    );


                /*
                |--------------------------------------------------------------------------
                | Existing Start Visit API.
                |--------------------------------------------------------------------------
                */

                await startVisit(
                    id,
                    {

                        latitude,

                        longitude,

                        address:
                            "",

                    }
                );


                /*
                |--------------------------------------------------------------------------
                | Save first route point.
                |
                | IMPORTANT:
                |
                | This happens AFTER Visit has successfully changed
                | to started status.
                |--------------------------------------------------------------------------
                */

                const startSaved =
                    await saveRoutePoint(
                        id,
                        position,
                        "start",
                        true
                    );


                /*
                |--------------------------------------------------------------------------
                | If start route point couldn't be saved,
                | Visit itself is already started.
                |
                | We still start tracking so the next point can
                | be captured.
                |--------------------------------------------------------------------------
                */

                if (!startSaved) {

                    console.warn(
                        "Initial START route point was not saved."
                    );

                }


                /*
                |--------------------------------------------------------------------------
                | Start continuous GPS tracking.
                |--------------------------------------------------------------------------
                */

                startGpsTracking(
                    id,
                    {
                        initialPosition:
                            position,
                    }
                );


                showSuccess(
                    "Visit started successfully. GPS tracking is now active."
                );


                /*
                |--------------------------------------------------------------------------
                | Reload UI.
                |--------------------------------------------------------------------------
                */

                if (
                    viewMode === "overview"
                ) {

                    await loadOverview();

                } else {

                    await loadVisits();

                }

            } catch (error) {

                console.error(
                    "START VISIT ERROR:",
                    error
                );


                /*
                |--------------------------------------------------------------------------
                | Browser GPS error.
                |--------------------------------------------------------------------------
                */

                if (
                    error?.code === 1
                ) {

                    showError(
                        "Location permission denied. Please allow GPS access."
                    );

                    return;

                }


                if (
                    error?.code === 2
                ) {

                    showError(
                        "Unable to determine your current location."
                    );

                    return;

                }


                if (
                    error?.code === 3
                ) {

                    showError(
                        "Location request timed out."
                    );

                    return;

                }


                showError(
                    error?.response?.data?.message ||
                    error?.message ||
                    "Unable to start visit."
                );

            }

        };


    // ============================================================
    // SUBMIT VISIT
    // ============================================================

    const handleSubmit = async (id) => {
        const confirmed = window.confirm(
            "Submit this visit for approval?"
        );

        if (!confirmed) {
            return;
        }

        stopGpsWatcher();

        try {
            // STEP 1: Capture final GPS position
            let finalPosition;

            try {
                finalPosition = await getCurrentPosition();
            } catch (gpsError) {
                console.error("FINAL GPS ERROR:", gpsError);

                startGpsTracking(id);

                if (gpsError?.code === 1) {
                    showError(
                        "Location permission denied. Please allow GPS access."
                    );
                } else if (gpsError?.code === 2) {
                    showError(
                        "Unable to determine final visit location."
                    );
                } else if (gpsError?.code === 3) {
                    showError(
                        "Final GPS location request timed out."
                    );
                } else {
                    showError(
                        "Unable to capture final GPS location."
                    );
                }

                return;
            }

            // STEP 2: Save final END point
            const endSaved = await saveRoutePoint(
                id,
                finalPosition,
                "end",
                true
            );

            if (!endSaved) {
                startGpsTracking(id);

                showError(
                    "Final route location could not be saved. Visit was not submitted. Please try again."
                );

                return;
            }

            // STEP 3: Submit visit only after END point is saved
            await submitVisit(id);

            // STEP 4: Clear tracking only after successful submission
            stopGpsTracking();

            showSuccess("Visit submitted successfully.");

            // STEP 5: Reload existing UI flow
            if (viewMode === "overview") {
                await loadOverview();
            } else {
                await loadVisits();
            }

        } catch (error) {
            console.error("SUBMIT VISIT ERROR:", error);

            // STEP 6: Verify actual backend status.
            // A network error can happen even if the server
            // successfully processed the submit request.
            try {
                const response = await getVisit(id);

                const visit =
                    response?.data?.visit ||
                    response?.data ||
                    response?.visit ||
                    null;

                if (
                    String(visit?.status).toLowerCase() === "started"
                ) {
                    // Visit is still active. Keep tracking and
                    // retain the saved end point for safe retry.
                    startGpsTracking(id);

                    showError(
                        error?.response?.data?.message ||
                        "Visit submission failed. Your visit is still active. Please retry."
                    );

                    return;
                }

                if (
                    String(visit?.status).toLowerCase() === "submitted"
                ) {
                    stopGpsTracking();

                    showSuccess(
                        "Visit was submitted successfully."
                    );

                    if (viewMode === "overview") {
                        await loadOverview();
                    } else {
                        await loadVisits();
                    }

                    return;
                }

                // Any other status: do not blindly restart tracking.
                stopGpsTracking();

                showError(
                    error?.response?.data?.message ||
                    "Visit status changed. Please refresh the page."
                );

            } catch (statusError) {
                console.error(
                    "Unable to verify visit status:",
                    statusError
                );

                // Preserve active visit ID in localStorage.
                // Next page load can attempt to recover tracking.
                try {
                    localStorage.setItem(
                        ACTIVE_TRACKING_KEY,
                        String(id)
                    );
                } catch (storageError) {
                    console.error(
                        "Unable to preserve active visit:",
                        storageError
                    );
                }

                startGpsTracking(id);

                showError(
                    "Unable to confirm submission. Please refresh and check the visit status before retrying."
                );
            }
        }
    };

    // ============================================================
    // APPROVE
    // ============================================================

    const handleApprove =
        async (
            id
        ) => {

            const confirmed =
                window.confirm(
                    "Approve this visit?"
                );


            if (!confirmed) {
                return;
            }


            try {

                await approveVisit(
                    id
                );


                showSuccess(
                    "Visit approved successfully."
                );


                if (
                    viewMode === "overview"
                ) {

                    await loadOverview();

                } else {

                    await loadVisits();

                }

            } catch (error) {

                console.error(
                    "APPROVE VISIT ERROR:",
                    error
                );


                showError(
                    error?.response?.data?.message ||
                    "Unable to approve visit."
                );

            }

        };


    // ============================================================
    // REJECT
    // ============================================================

    const handleReject =
        async (
            id
        ) => {

            const reason =
                window.prompt(
                    "Enter rejection reason"
                );


            if (
                !reason ||
                !reason.trim()
            ) {

                return;

            }


            try {

                await rejectVisit(
                    id,
                    reason.trim()
                );


                showSuccess(
                    "Visit rejected successfully."
                );


                if (
                    viewMode === "overview"
                ) {

                    await loadOverview();

                } else {

                    await loadVisits();

                }

            } catch (error) {

                console.error(
                    "REJECT VISIT ERROR:",
                    error
                );


                showError(
                    error?.response?.data?.message ||
                    "Unable to reject visit."
                );

            }

        };


    // ============================================================
    // CORRECTION
    // ============================================================

    const handleCorrection =
        async (
            id
        ) => {

            const reason =
                window.prompt(
                    "Enter correction reason"
                );


            if (
                !reason ||
                !reason.trim()
            ) {

                return;

            }


            try {

                await requestVisitCorrection(
                    id,
                    reason.trim()
                );


                showSuccess(
                    "Correction requested successfully."
                );


                if (
                    viewMode === "overview"
                ) {

                    await loadOverview();

                } else {

                    await loadVisits();

                }

            } catch (error) {

                console.error(
                    "CORRECTION ERROR:",
                    error
                );


                showError(
                    error?.response?.data?.message ||
                    "Unable to request correction."
                );

            }

        };


    // ============================================================
    // EDIT
    // ============================================================

    
    const handleEdit = async (visit) => {
        if (!visit?.id) {
            showError("Visit ID is missing.");
            return;
        }

        try {
            setLoading(true);

            // Load the complete visit, not only the table row.
            const response = await getVisit(visit.id);

            const fullVisit =
                response?.data?.visit ||
                response?.visit ||
                response?.data ||
                response;

            if (!fullVisit?.id) {
                showError("Unable to load visit details.");
                return;
            }

            setEditingVisit(fullVisit);
            setOpenModal(true);
        } catch (error) {
            console.error("Load visit for edit error:", error);
            showError(
                error?.response?.data?.message ||
                "Unable to load visit details."
            );
        } finally {
            setLoading(false);
        }
    };



    // ============================================================
    // RENDER
    // ============================================================

    return (

        <div className="mt-5">

            {/* ==================================================
                HEADER
            ================================================== */}

            <div
                className="
                    mb-5
                    flex
                    flex-col
                    gap-3
                    rounded-xl
                    border
                    bg-white
                    p-4
                    shadow-sm
                    md:flex-row
                    md:items-center
                    md:justify-between
                "
            >

                <div>

                    <h2
                        className="
                            text-lg
                            font-semibold
                            text-gray-800
                        "
                    >
                        Visits
                    </h2>


                    <p
                        className="
                            mt-1
                            text-xs
                            text-gray-500
                        "
                    >
                        Manage salesperson visits,
                        working details, expenses and approvals.
                    </p>

                </div>


                <div
                    className="
                        flex
                        items-center
                        gap-2
                    "
                >

                    <button
                        type="button"
                        onClick={() =>
                            setViewMode(
                                "overview"
                            )
                        }
                        className={`
                            rounded-lg
                            px-4
                            py-2
                            text-sm
                            font-medium
                            ${
                                viewMode === "overview"
                                    ? "bg-blue-600 text-white"
                                    : "border bg-white text-gray-700 hover:bg-gray-50"
                            }
                        `}
                    >
                        Salesperson Overview
                    </button>


                    <button
                        type="button"
                        onClick={() =>
                            setViewMode(
                                "visits"
                            )
                        }
                        className={`
                            rounded-lg
                            px-4
                            py-2
                            text-sm
                            font-medium
                            ${
                                viewMode === "visits"
                                    ? "bg-blue-600 text-white"
                                    : "border bg-white text-gray-700 hover:bg-gray-50"
                            }
                        `}
                    >
                        All Visits
                    </button>

                </div>

            </div>


            {/* ==================================================
                FILTER BAR
            ================================================== */}

            <div
                className="
                    mb-5
                    rounded-xl
                    border
                    bg-white
                    p-4
                    shadow-sm
                "
            >

                <div
                    className="
                        mb-4
                        flex
                        items-center
                        justify-between
                    "
                >

                    <div>

                        <h3
                            className="
                                text-base
                                font-semibold
                                text-gray-800
                            "
                        >
                            Visit Filters
                        </h3>


                        <p
                            className="
                                mt-1
                                text-xs
                                text-gray-500
                            "
                        >
                            Filter visits by salesperson,
                            date range and status.
                        </p>

                    </div>


                    <button
                        type="button"
                        onClick={
                            clearFilters
                        }
                        className="
                            rounded-lg
                            border
                            px-4
                            py-2
                            text-sm
                            font-medium
                            text-gray-700
                            hover:bg-gray-50
                        "
                    >
                        Clear Filters
                    </button>

                </div>


                <div
                    className="
                        grid
                        grid-cols-1
                        gap-4
                        md:grid-cols-2
                        lg:grid-cols-4
                    "
                >

                    {/* SALESPERSON */}

                    <div>

                        <label
                            className="
                                mb-1
                                block
                                text-sm
                                font-medium
                                text-gray-700
                            "
                        >
                            Salesperson
                        </label>


                        <select
                            value={
                                filters.employeeId
                            }
                            onChange={(e) =>
                                handleFilterChange(
                                    "employeeId",
                                    e.target.value
                                )
                            }
                            disabled={
                                isSalesPerson ||
                                employeeLoading
                            }
                            className="
                                w-full
                                rounded-lg
                                border
                                border-gray-300
                                bg-white
                                px-3
                                py-2.5
                                text-sm
                                outline-none
                                focus:border-blue-500
                            "
                        >

                            {employeeOptions.map(
                                (
                                    employee
                                ) => (

                                    <option
                                        key={
                                            employee.value ||
                                            "all-salespersons"
                                        }
                                        value={
                                            employee.value
                                        }
                                    >
                                        {
                                            employee.label
                                        }
                                    </option>

                                )
                            )}

                        </select>

                    </div>


                    {/* FROM DATE */}

                    <div>

                        <label
                            className="
                                mb-1
                                block
                                text-sm
                                font-medium
                                text-gray-700
                            "
                        >
                            From Date
                        </label>


                        <input
                            type="date"
                            value={
                                filters.fromDate
                            }
                            max={
                                filters.toDate ||
                                undefined
                            }
                            onChange={(e) =>
                                handleFilterChange(
                                    "fromDate",
                                    e.target.value
                                )
                            }
                            className="
                                w-full
                                rounded-lg
                                border
                                border-gray-300
                                bg-white
                                px-3
                                py-2.5
                                text-sm
                                outline-none
                                focus:border-blue-500
                            "
                        />

                    </div>


                    {/* TO DATE */}

                    <div>

                        <label
                            className="
                                mb-1
                                block
                                text-sm
                                font-medium
                                text-gray-700
                            "
                        >
                            To Date
                        </label>


                        <input
                            type="date"
                            value={
                                filters.toDate
                            }
                            min={
                                filters.fromDate ||
                                undefined
                            }
                            onChange={(e) =>
                                handleFilterChange(
                                    "toDate",
                                    e.target.value
                                )
                            }
                            className="
                                w-full
                                rounded-lg
                                border
                                border-gray-300
                                bg-white
                                px-3
                                py-2.5
                                text-sm
                                outline-none
                                focus:border-blue-500
                            "
                        />

                    </div>


                    {/* STATUS */}

                    <div>

                        <label
                            className="
                                mb-1
                                block
                                text-sm
                                font-medium
                                text-gray-700
                            "
                        >
                            Status
                        </label>


                        <select
                            value={
                                filters.status
                            }
                            onChange={(e) =>
                                handleFilterChange(
                                    "status",
                                    e.target.value
                                )
                            }
                            className="
                                w-full
                                rounded-lg
                                border
                                border-gray-300
                                bg-white
                                px-3
                                py-2.5
                                text-sm
                                outline-none
                                focus:border-blue-500
                            "
                        >

                            {statusOptions.map(
                                (
                                    option
                                ) => (

                                    <option
                                        key={
                                            option.value ||
                                            "all-status"
                                        }
                                        value={
                                            option.value
                                        }
                                    >
                                        {
                                            option.label
                                        }
                                    </option>

                                )
                            )}

                        </select>

                    </div>

                </div>

            </div>


            {/* ==================================================
                OVERVIEW
            ================================================== */}

            {viewMode === "overview" && (

                <DataTable

                    title="Salesperson Visit Overview"

                    columns={

                        overviewColumns({

                            onView:
                                handleViewEmployee,

                        }).filter(
                            (column) =>
                                column.id !==
                                "totalKm"
                        )

                    }

                    data={
                        overview
                    }

                    loading={
                        loading
                    }

                    onAdd={() => {
                        setEditingVisit(null);
                        setOpenModal(true);
                    }}

                    addButtonText="Create Visit"

                />

            )}


            {/* ==================================================
                VISITS
            ================================================== */}

            {viewMode === "visits" && (

                <>

                    <div
                        className="
                            mb-3
                            flex
                            items-center
                            justify-between
                        "
                    >

                        <button
                            type="button"
                            onClick={
                                handleBackToOverview
                            }
                            className="
                                rounded-lg
                                border
                                bg-white
                                px-4
                                py-2
                                text-sm
                                font-medium
                                text-gray-700
                                hover:bg-gray-50
                            "
                        >
                            ← Back to Overview
                        </button>

                    </div>


                    <DataTable

                        title="Visits"

                        columns={

                            visitColumns({

                                onDelete:
                                    handleDelete,

                                onStart:
                                    handleStart,

                                onSubmit:
                                    handleSubmit,

                                onApprove:
                                    handleApprove,

                                onReject:
                                    handleReject,

                                onCorrection:
                                    handleCorrection,

                                onEdit:
                                    handleEdit,

                            })

                        }

                        data={
                            visits
                        }

                        loading={
                            loading
                        }

                        onAdd={() => {
                            setEditingVisit(null);
                            setOpenModal(true);
                        }}

                        addButtonText="Create Visit"

                    />

                </>

            )}


            {/* ==================================================
                CREATE VISIT MODAL
            ================================================== */}

            <VisitModal
                open={openModal}
                visit={editingVisit}
                onClose={() => {
                    setOpenModal(false);
                    setEditingVisit(null);
                }}
                reload={
                    viewMode === "overview"
                        ? loadOverview
                        : loadVisits
                }
            />

        </div>

    );

}